import React from 'react';
import { Link } from 'react-router-dom';
import { getIslandCapabilities } from 'alakai-aloha-shared';
import Icon from '../shared/IconLibrary/Icon';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const BrandExplainer = () => {
  const { island } = React.useMemo(() => getRuntimeSiteContext(), []);
  const capabilities = React.useMemo(() => getIslandCapabilities(island), [island]);

  return (
    <section className="text-center">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
          "Alakaʻi Aloha" Defined
        </h2>
        <p className="text-lg md:text-xl mb-4 max-w-3xl mx-auto leading-relaxed text-center">
          Our name is a combination of two Hawaiian words that express our promise to guide you with warmth, wisdom, and respect.
        </p>          
        
        <div className="flex flex-col items-center">
          {/* Dictionary Boxes */}
          <div className="flex flex-col md:flex-row gap-8 justify-center text-left mb-8">
            {/* Dictionary Entry 1 */}
            <div className="w-[350px] pt-2 pr-1 pb-2 pl-2 rounded-lg shadow-xl relative overflow-hidden" style={{
              backgroundImage: 'url(/images/textured-paper.PNG)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}>
              {/* Subtle overlay to enhance texture contrast */}
              <div className="absolute inset-0 bg-gray-500/10 rounded-lg"></div>
              <div className="flex gap-0 relative z-10">
                {/* Left column - Text content */}
                <div className="flex-1">
                  {/* Dictionary-style header */}
                  <div className="border-b border-gray-800 pb-1 mb-1">
                    <h3 className="text-3xl font-serif font-bold text-gray-800 mb-1">
                      alakaʻi
                      <span className="text-lg font-normal text-gray-600 ml-2">
                        /ah-lah-KAH-ee/
                      </span>
                    </h3>
                  </div>
                  
                  <div className="border-b border-dashed border-gray-800 pb-1 mb-1">
                    <h4 className="text-lg font-mono font-bold text-gray-700 tracking-tighter">
                      noun: guide, leader, mentor
                    </h4>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed font-mono tracking-tight">
                    A person who leads with intention and care. Often used to describe someone with responsibility, direction, and cultural influence.
                  </p>
                </div>
                
                {/* Right column - Icons and flag */}
                <div className="w-16 flex flex-col justify-between items-end pr-1">
                  {/* Top-aligned icon */}
                  <div className="flex justify-center">
                    <Icon name="simple-compass" size={64} className="text-gray-600 [&>*]:fill-gray-600" />
                  </div>
                  
                  {/* Bottom-aligned flag and origin */}
                  <div className="flex flex-col items-center">
                    <img 
                      src="/images/Flag_of_Hawaii.png" 
                      alt="Hawaiian flag" 
                      className="w-12 h-8 object-contain mb-1 opacity-70"
                    />
                    <div className="text-xs text-gray-700 italic text-center leading-tight">
                      Hawaiian<br/>origin
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Dictionary Entry 2 */}
            <div className="w-[350px] pt-2 pr-1 pb-2 pl-2 rounded-lg shadow-xl relative overflow-hidden" style={{
              backgroundImage: 'url(/images/textured-paper.PNG)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}>
              {/* Subtle overlay to enhance texture contrast */}
              <div className="absolute inset-0 bg-gray-500/10 rounded-lg"></div>
              <div className="flex gap-0 relative z-10">
                {/* Left column - Text content */}
                <div className="flex-1">
                  {/* Dictionary-style header */}
                  <div className="border-b border-gray-800 pb-1 mb-1">
                    <h3 className="text-3xl font-serif font-bold text-gray-800 mb-1">
                      aloha
                      <span className="text-lg font-normal text-gray-600 ml-2">
                        /ah-LOH-hah/
                      </span>
                    </h3>
                  </div>
                  
                  <div className="border-b border-dashed border-gray-800 pb-1 mb-1">
                    <h4 className="text-lg font-mono font-bold text-gray-700 tracking-tighter">
                      noun: love, care, respect
                    </h4>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed font-mono tracking-tight">
                    A way of being rooted in kindness, hospitality, and harmony. Often expressed as both a greeting and a deeper cultural value.
                  </p>
                </div>
                
                {/* Right column - Icons and flag */}
                <div className="w-16 flex flex-col justify-between items-end pr-1">
                  {/* Top-aligned icon */}
                  <div className="flex justify-center">
                    <Icon name="flower" size={56} className="text-gray-600 [&>*]:fill-gray-600" />
                  </div>
                  
                  {/* Bottom-aligned flag and origin */}
                  <div className="flex flex-col items-center">
                    <img 
                      src="/images/Flag_of_Hawaii.png" 
                      alt="Hawaiian flag" 
                      className="w-12 h-8 object-contain mb-1 opacity-70"
                    />
                    <div className="text-xs text-gray-700 italic text-center leading-tight">
                      Hawaiian<br/>origin
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-6 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-center lg:gap-4 max-w-3xl mx-auto">
          <Link
            to="/about"
            className="btn-secondary w-full text-center lg:w-auto lg:min-w-64"
          >
            More About Us
          </Link>
          {capabilities.tripPlanner ? (
            <Link
              to="/interview"
              className="btn-primary-cta w-full lg:flex-1 text-center"
            >
              Let Us Be Your Alakaʻi
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default BrandExplainer;
